import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    createDownload,
    createDownloadCategory,
    createDownloadLanguage,
    deleteDownload,
    deleteDownloadCategory,
    deleteDownloadLanguage,
    editDownloadCategory,
    fetchDownload,
    fetchDownloadCategory,
    fetchDownloadLanguage,
    updateDownload,
} from 'src/api/download-admin';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    CREATE_DOWNLOAD,
    CREATE_DOWNLOAD_CATEGORY,
    CREATE_DOWNLOAD_LANGUAGE,
    DELETE_DOWNLOAD,
    DELETE_DOWNLOAD_CATEGORY,
    DELETE_DOWNLOAD_LANGUAGE,
    EDIT_DOWNLOAD_CATEGORY,
    FETCH_DOWNLOADS,
    FETCH_DOWNLOAD_CATEGORIES,
    FETCH_DOWNLOAD_LANGUAGE,
    requestCreateDownload,
    requestCreateDownloadCategory,
    requestCreateDownloadCategoryFailure,
    requestCreateDownloadCategorySuccess,
    requestCreateDownloadFailure,
    requestCreateDownloadLanguage,
    requestCreateDownloadLanguageFailure,
    requestCreateDownloadLanguageSuccess,
    requestCreateDownloadSuccess,
    requestDeleteDownload,
    requestDeleteDownloadCategoryById,
    requestDeleteDownloadCategoryByIdFailure,
    requestDeleteDownloadCategoryByIdSuccess,
    requestDeleteDownloadFailure,
    requestDeleteDownloadLanguageById,
    requestDeleteDownloadLanguageByIdFailure,
    requestDeleteDownloadLanguageByIdSuccess,
    requestDeleteDownloadSuccess,
    requestDownloadCategories,
    requestDownloadCategoriesFailure,
    requestDownloadCategoriesSuccess,
    requestDownloadLanguage,
    requestDownloadLanguageFailure,
    requestDownloadLanguageSuccess,
    requestDownloads,
    requestDownloadsFailure,
    requestDownloadsSuccess,
    requestEditDownloadCategory,
    requestEditDownloadCategoryFailure,
    requestEditDownloadCategorySuccess,
    requestUpdateDownload,
    requestUpdateDownloadFailure,
    requestUpdateDownloadSuccess,
    UPDATE_DOWNLOAD,
} from './actions';

/* Download Category */
function* fetchDownloadCategoriesSaga(action: AnyAction): SagaIterator {
    yield put(requestDownloadCategories());

    const { error, response } = yield call(fetchDownloadCategory);

    if (error) yield put(requestDownloadCategoriesFailure(error));
    else yield put(requestDownloadCategoriesSuccess(response));
}

function* createDownloadCategorySaga(action: AnyAction): SagaIterator {
    yield put(requestCreateDownloadCategory());

    const { error, response } = yield call(createDownloadCategory, action.newDownloadCategory);

    if (error) yield put(requestCreateDownloadCategoryFailure(error));
    else yield put(requestCreateDownloadCategorySuccess(response));
}

function* editDownloadCategorySaga(action: AnyAction): SagaIterator {
    yield put(requestEditDownloadCategory());

    const { error, response } = yield call(editDownloadCategory, action.newDownloadCategory);

    if (error) {
        yield put(requestEditDownloadCategoryFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.editDownloadCategoryFailure') +
                    ' \n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestEditDownloadCategorySuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.editDownloadCategorySuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* deleteDownloadCategorySaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteDownloadCategoryById());

    const { error, response } = yield call(deleteDownloadCategory, action.downloadCategoryId);

    if (error) yield put(requestDeleteDownloadCategoryByIdFailure(error));
    else yield put(requestDeleteDownloadCategoryByIdSuccess(response));
}

export function* generateFetchDownloadCategoriesWatcher() {
    yield takeLatest(FETCH_DOWNLOAD_CATEGORIES, fetchDownloadCategoriesSaga);
}
export function* generateCreateDownloadCategoryWatcher() {
    yield takeLatest(CREATE_DOWNLOAD_CATEGORY, createDownloadCategorySaga);
}
export function* generateEditDownloadCategoryWatcher() {
    yield takeLatest(EDIT_DOWNLOAD_CATEGORY, editDownloadCategorySaga);
}
export function* generateDeleteDownloadCategoryWatcher() {
    yield takeLatest(DELETE_DOWNLOAD_CATEGORY, deleteDownloadCategorySaga);
}

/* Download */
function* fetchDownloadsSaga(action: AnyAction): SagaIterator {
    yield put(requestDownloads());

    const { error, response } = yield call(fetchDownload, action.downloadCategoryNormalizedName, action.pageable);

    if (error) {
        yield put(requestDownloadsFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.fetchDownloadsFailure') +
                    ' \n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDownloadsSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.fetchDownloadsSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* createDownloadSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateDownload());

    const { error, response } = yield call(createDownload, action.newDownload);

    if (error) {
        yield put(requestCreateDownloadFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.createDownloadFailure') +
                    ' \n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestCreateDownloadSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.createDownloadSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* updateDownloadSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateDownload());

    const { error, response } = yield call(updateDownload, action.newDownload);

    if (error) {
        yield put(requestUpdateDownloadFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.editDownloadFailure') + ' \n' + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestUpdateDownloadSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.editDownloadSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* deleteDownloadSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteDownload());

    const { error, response } = yield call(deleteDownload, action.downloadId);

    if (error) {
        yield put(requestDeleteDownloadFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.deleteDownloadFailure') +
                    ' \n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeleteDownloadSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.deleteDownloadSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchDownloadsWatcher() {
    yield takeLatest(FETCH_DOWNLOADS, fetchDownloadsSaga);
}
export function* generateCreateDownloadWatcher() {
    yield takeLatest(CREATE_DOWNLOAD, createDownloadSaga);
}
export function* generateUpdateDownloadWatcher() {
    yield takeLatest(UPDATE_DOWNLOAD, updateDownloadSaga);
}
export function* generateDeleteDownloadWatcher() {
    yield takeLatest(DELETE_DOWNLOAD, deleteDownloadSaga);
}

/* Download Language */
function* fetchDownloadLanguagesSaga(action: AnyAction): SagaIterator {
    yield put(requestDownloadLanguage());

    const { error, response } = yield call(fetchDownloadLanguage);

    if (error) {
        yield put(requestDownloadLanguageFailure(error));
    } else {
        yield put(requestDownloadLanguageSuccess(response));
    }
}

function* createDownloadLanguageSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateDownloadLanguage());

    const { error, response } = yield call(
        createDownloadLanguage,
        action.downloadId,
        action.languageIso2,
        action.file,
        action.countryIso2
    );

    if (error) {
        yield put(requestCreateDownloadLanguageFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.createDownloadLanguageFailure') +
                    ' \n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestCreateDownloadLanguageSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.createDownloadLanguageSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* deleteDownloadLanguageSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteDownloadLanguageById());

    const { error, response } = yield call(deleteDownloadLanguage, action.downloadLanguageId);

    if (error) {
        yield put(requestDeleteDownloadLanguageByIdFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('office:pages.download_management.messages.deleteDownloadLanguageFailure') +
                    ' \n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeleteDownloadLanguageByIdSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('office:pages.download_management.messages.deleteDownloadLanguageSuccess'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchDownloadLanguagesWatcher() {
    yield takeLatest(FETCH_DOWNLOAD_LANGUAGE, fetchDownloadLanguagesSaga);
}
export function* generateCreateDownloadLanguageWatcher() {
    yield takeLatest(CREATE_DOWNLOAD_LANGUAGE, createDownloadLanguageSaga);
}
export function* generateDeleteDownloadLanguageWatcher() {
    yield takeLatest(DELETE_DOWNLOAD_LANGUAGE, deleteDownloadLanguageSaga);
}
